import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAndyNotifications } from "@/features/notifications/viewModal/useAndyNotifications";
import styles from "./NotificationListPage.module.css";
import { Gifs } from "@/assets/gifs";

type NotificationListPageProps = {
  type: "notification" | "chat";
  title: string;
};

const getNotificationText = (value: unknown) => {
  return typeof value === "string" && value.trim() ? value : null;
};

export const NotificationListPage = ({ type, title }: NotificationListPageProps) => {
  const navigate = useNavigate();
  const { notifications, loading, unreadCount, markAllAsRead } = useAndyNotifications(type);

  const formatted = useMemo(
    () =>
      notifications.map((item, index) => {
        const header =
          getNotificationText(item.title) ??
          getNotificationText(item.tittle) ??
          getNotificationText(item.subject) ??
          `${title} #${index + 1}`;
        const body =
          getNotificationText(item.body) ??
          getNotificationText(item.description) ??
          getNotificationText(item.message) ??
          getNotificationText(item.content) ??
          "No additional details.";
        const dateRaw =
          item.created_at ?? item.createdAt ?? item.sent_at ?? item.sentAt ?? item.date;
        const date = dateRaw ? new Date(String(dateRaw)).toLocaleString() : "";
        const isRead = Boolean(item.read ?? item.isRead ?? item.is_read ?? item.is_seen);
        const key = String(item.id ?? item.notificationId ?? `${type}-${index}`);

        return { key, header, body, date, isRead };
      }),
    [notifications, title, type]
  );
  const canMarkAllAsRead = unreadCount > 0;

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
            ← Back
          </button>
          <h1 className={styles.title}>{title}</h1>
        </div>
        <button
          type="button"
          className={styles.markReadBtn}
          onClick={() => void markAllAsRead()}
          disabled={!canMarkAllAsRead}
        >
          Mark all as read
        </button>
      </header>

      {loading ? (
        <div className={styles.empty}>Loading notifications...</div>
      ) : formatted.length === 0 ? (
        <div className={styles.empty}>
          <img src={Gifs.andyWalking} alt="Andy walking" className={styles.emptyGif} />
          <div>No notifications found.</div>
        </div>
      ) : (
        <section className={styles.list} aria-label={`${title} list`}>
          {formatted.map((item) => (
            <article
              key={item.key}
              className={`${styles.card} ${!item.isRead ? styles.cardUnread : ""}`}
            >
              <h2 className={styles.itemTitle}>{item.header}</h2>
              <p className={styles.body}>{item.body}</p>
              {item.date && <div className={styles.meta}>{item.date}</div>}
            </article>
          ))}
        </section>
      )}
    </main>
  );
};
